import React, { FC, useEffect, useMemo, useState } from 'react';
import Header from '../../components/layout/header/Header';
import { Button, Card, Container } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import useOrganizationsList from '../../hooks/api/organization/useOrganizationsList';
import AddOrganizationModal from '../../components/layout/navbar/organizationMenu/addOrganizationModal/AddOrganizationModal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { history } from '../../history';
import { setOrganization } from '../../slices/organization';
import Avatar from '../../components/layout/avatar/Avatar';
import LoadingSpinner from '../../components/layout/loadingSpinner/LoadingSpinner';
import SearchInput from '../../components/library/inputs/search/SearchInput';
import PaginationComponent from '../../components/pagination/PaginationComponent';
import { DefaultPage } from '../../components/layout/navbar/Navbar';
import EmptyList from '../../components/layout/emptyList/EmptyList';

const OrganizationSelection: FC = () => {
  const [showModalOrganization, setShowModalOrganization] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const { getOrganizations, isLoading } = useOrganizationsList();
  const organizations = useSelector((state: RootState) => state.organization?.organizations);
  const intl = useIntl();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const pageSize = 10;

  const filteredOrganizations = useMemo(() => {
    const displayOrganizations = organizations?.filter((organization) =>
      (organization.name || '').toLowerCase().includes(search.toLowerCase()),
    );

    setTotalCount(displayOrganizations?.length || 0);
    setCurrentPage(1);

    return displayOrganizations;
  }, [organizations, search]);

  const paginatedOrganizations = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredOrganizations ? filteredOrganizations.slice(startIndex, endIndex) : [];
  }, [filteredOrganizations, currentPage]);

  useEffect(() => {
    getOrganizations();
  }, []);

  const handleSelectedOrganization = (organization) => {
    dispatch(setOrganization(organization));
    history.replace(DefaultPage);
  };

  return (
    <React.Fragment>
      <Header></Header>
      <div className="organization-selection">
        <div className="page-content">
          {isLoading && <LoadingSpinner />}
          {!isLoading && (
            <Container fluid>
              <div className="title-wrapper">
                <span>{intl.formatMessage({ id: 'organizationPage.selection.title' })}</span>
              </div>
              <div className="organization-wrapper mb-2">
                <div className="row">
                  <div className="d-flex align-items-center flex-row">
                    <SearchInput
                      value={search}
                      className={'shadow flex-fill me-2'}
                      onChange={(e) => setSearch(e.currentTarget.value)}
                    ></SearchInput>
                  </div>
                  {paginatedOrganizations.length === 0 && (
                    <EmptyList message={intl.formatMessage({ id: 'organizationPage.selection.noOrganizations' })} />
                  )}
                  {paginatedOrganizations?.map((organization) => {
                    return (
                      <div className="organization" key={organization.id}>
                        <Card
                          className="d-flex flex-row justify-content-between align-items-center px-3 py-2"
                          onClick={() => {
                            handleSelectedOrganization(organization);
                          }}
                        >
                          <div className="d-flex flex-row align-items-center">
                            <div className="image-wrapper me-4">
                              <div className="avatar-wrapper">
                                <Avatar src={organization.avatar} width={40} height={40}></Avatar>
                              </div>
                            </div>
                            <div className="name-wrapper">
                              <span className="organization-name">{organization.name}</span>
                            </div>
                          </div>
                          <div>
                            <span className="mdi mdi-chevron-right chevron-icon fs-4"></span>
                          </div>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
              {!isLoading &&
                filteredOrganizations &&
                filteredOrganizations.length > 0 &&
                Math.ceil(totalCount / pageSize) > 1 && (
                  <PaginationComponent
                    currentPage={currentPage}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    onChange={(val) => setCurrentPage(val)}
                  />
                )}
              <div className="button-wrapper">
                <Button onClick={() => setShowModalOrganization(true)}>
                  <span className="mdi mdi-plus"></span>
                  {intl.formatMessage({ id: 'organizationPage.selection.addOrganization' })}
                </Button>
              </div>
            </Container>
          )}
        </div>
      </div>
      <AddOrganizationModal
        show={showModalOrganization}
        onClose={() => {
          setShowModalOrganization(false);
        }}
      ></AddOrganizationModal>
    </React.Fragment>
  );
};

export default OrganizationSelection;
